import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Select,
  Button,
  Typography,
  message,
  Space,
  Alert,
  Checkbox
} from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { FileExcelOutlined } from '@ant-design/icons';
import { customerAPI, warehouseAPI, reportAPI } from '../services/api.ts';

const { Title } = Typography;
const { Option } = Select;

interface Customer {
  id: number;
  company: string;
  customer_code?: string;
}

interface Warehouse {
  id: number;
  description: string;
  cod_mag?: string;
}

const ReportGiacenze: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>(undefined);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | undefined>(undefined);
  const [onlyWithStock, setOnlyWithStock] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    loadOptions();
  }, []);

  const loadOptions = async () => {
    setLoading(true);
    try {
      const [customersData, warehousesData] = await Promise.all([
        customerAPI.getCustomers(),
        warehouseAPI.getWarehouses()
      ]);
      setCustomers(customersData);
      setWarehouses(warehousesData);
    } catch (error) {
      console.error('Errore caricamento opzioni:', error);
      message.error('Errore nel caricamento delle opzioni');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      await reportAPI.exportGiacenzeExcel(selectedCustomerId, selectedWarehouseId, onlyWithStock);
      message.success('Report Excel generato con successo');
    } catch (error: any) {
      console.error('Errore generazione report:', error);
      const errorMessage = error.response?.data?.detail || 'Errore durante la generazione del report';
      message.error(errorMessage);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={2} style={{ marginBottom: '24px' }}>Report Giacenze</Title>

        <Alert
          message="Con un cliente selezionato il report include tutti i prodotti associati a quel cliente (anche senza giacenza). Senza cliente vengono inclusi TUTTI i prodotti, anche quelli non associati ad alcun cliente, per vedere l'intero magazzino/transito/produzione. Le quantità mostrate sono i totali di prodotto. Il filtro magazzino è opzionale."
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />

        <Row gutter={[16, 16]} align="bottom">
          <Col xs={24} sm={10} md={8}>
            <div style={{ marginBottom: '4px', fontWeight: 500 }}>Cliente (opzionale)</div>
            <Select
              placeholder="Tutti i prodotti (nessun cliente)"
              showSearch
              allowClear
              style={{ width: '100%' }}
              value={selectedCustomerId}
              onChange={(value) => setSelectedCustomerId(value)}
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
              loading={loading}
            >
              {customers.map(customer => (
                <Option key={customer.id} value={customer.id}>
                  {customer.company}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={10} md={8}>
            <div style={{ marginBottom: '4px', fontWeight: 500 }}>Magazzino (opzionale)</div>
            <Select
              placeholder="Tutti i magazzini"
              showSearch
              allowClear
              style={{ width: '100%' }}
              value={selectedWarehouseId}
              onChange={(value) => setSelectedWarehouseId(value)}
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
              loading={loading}
            >
              {warehouses.map(warehouse => (
                <Option key={warehouse.id} value={warehouse.id}>
                  {warehouse.description}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={4} md={8}>
            <Space>
              <Button
                type="primary"
                icon={<FileExcelOutlined />}
                onClick={handleExport}
                loading={exportLoading}
                size="large"
              >
                Genera Report Excel
              </Button>
            </Space>
          </Col>
        </Row>

        <Row style={{ marginTop: '16px' }}>
          <Col span={24}>
            <Checkbox
              checked={onlyWithStock}
              onChange={(e: CheckboxChangeEvent) => setOnlyWithStock(e.target.checked)}
            >
              Escludi i prodotti senza giacenza (disponibile + transito + produzione = 0)
            </Checkbox>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default ReportGiacenze;
